import * as React from 'react';
import { useAuthStore } from '@/auth';
import { Auth } from 'firebase/auth';
import { getCustomerSubscription } from '../services/get-customer-subscription';
import { useSubscriptionStore } from '../store/subscription-store';
import { getSubscriptions } from '../services/get-subscriptions';
import { updateCustomerSubscription } from '../services/update-customer-subscription';
import { isRight, isLeft } from '@shared-commons/utils';
import { cancelCustomerSubscription } from '../services/cancel-customer-subscription';
import { createSubscriptionCheckoutSession } from '../services/create-sub-checkout-session';

export const useSubscription = (auth: Auth) => {
  const {
    customerSubscription,
    setCustomerSubscription,
    subscriptionsOptions,
    setSubscriptionsOptions,
    setSubscriptionsLoading,
    subscriptionsLoading,
  } = useSubscriptionStore();
  const { user } = useAuthStore();
  const [error, setError] = React.useState<Error | null>(null);

  const getCustomerSub = React.useCallback(async () => {
    const accessToken = await auth.currentUser!.getIdToken(true);
    const result = await getCustomerSubscription(user?.email, accessToken)();
    if (isRight(result)) {
      setCustomerSubscription(result.value);
      setSubscriptionsLoading(false);
    } else if (isLeft(result)) {
      const error = new Error(result.value?.message || 'Errore durante il recupero del profilo');
      setError(error);
      setSubscriptionsLoading(false);
    }
  }, [user?.email, setCustomerSubscription, setSubscriptionsLoading, auth]);

  const getSubs = React.useCallback(async () => {
    const accessToken = await auth.currentUser!.getIdToken(true);
    const result = await getSubscriptions(accessToken)();
    if (isRight(result)) {
      setSubscriptionsOptions(result.value);
      setSubscriptionsLoading(false);
    } else if (isLeft(result)) {
      const error = new Error(result.value?.message || 'Errore durante il recupero del profilo');
      setError(error);
      setSubscriptionsLoading(false);
    }
  }, [setSubscriptionsOptions, setSubscriptionsLoading, auth]);

  const createSubscription = React.useCallback(
    ({ priceId, trialPeriodDays }: { priceId: string; trialPeriodDays?: number | null }) => {
      if (typeof window === 'undefined') return;

      createSubscriptionCheckoutSession({
        userUid: user?.uid || '',
        successUrl: `${window.location.origin}/order-confirmation`,
        cancelUrl: window.location.href,
        priceId,
        trialPeriodDays,
      });
    },
    [user],
  );

  const updateSubscription = React.useCallback(
    async (name: string) => {
      const accessToken = await auth.currentUser!.getIdToken(true);
      const result = await updateCustomerSubscription({
        email: user?.email,
        newSubscriptionName: name,
        accessToken,
        userUid: user?.uid || '',
      })();
      if (isRight(result)) {
        const data = await result.value;
        if (data && typeof window !== 'undefined') {
          window.location.href = data; // Reindirizza l'utente al Customer Portal per modificare la subscription
        }
        setSubscriptionsLoading(false);
      } else if (isLeft(result)) {
        const error = new Error(result.value?.message || 'Errore aggiornarmento del profilo');
        setError(error);
        setSubscriptionsLoading(false);
      }
    },
    [setSubscriptionsLoading, user?.email, auth],
  );

  const cancelSubscription = React.useCallback(async () => {
    const accessToken = await auth.currentUser!.getIdToken(true);
    const result = await cancelCustomerSubscription({
      email: user?.email,
      accessToken,
      userUid: user?.uid || '',
    })();
    if (isRight(result)) {
      const data = await result.value;
      if (data && typeof window !== 'undefined') {
        window.location.href = data; // Reindirizza l'utente al Customer Portal per cancellare la subscription
      }
      setSubscriptionsLoading(false);
    } else if (isLeft(result)) {
      const error = new Error(result.value?.message || 'Errore aggiornarmento del profilo');
      setError(error);
      setSubscriptionsLoading(false);
    }
  }, [setSubscriptionsLoading, user?.email, auth]);

  return {
    subscriptionsLoading,
    error,
    customerSubscription,
    getCustomerSub,
    updateSubscription,
    subscriptionsOptions,
    getSubs,
    cancelSubscription,
    createSubscription,
  };
};
